
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';
import { authClient } from '@/lib/auth-client';

export type Language = 'en' | 'es' | 'jp' | 'fr-ca' | 'pt-br' | 'de' | 'zh';
export type Theme = 'light' | 'dark';

export interface ColorScheme {
  primary: string;
  secondary: string;
  accent: string;
  chartColors: string[];
}

export interface LinkedAccountsStatus {
  plaid: 'connected' | 'disconnected' | 'error';
  belvo: 'connected' | 'disconnected' | 'error';
}

export interface PaymentProvidersStatus {
  currencycloud: 'connected' | 'disconnected' | 'error';
  efex: 'connected' | 'disconnected' | 'error';
  thunes: 'connected' | 'disconnected' | 'error';
}

export interface AgentMonitoringSettings {
  enabled: boolean;
  scanInterval: number; // minutes
  confidenceThreshold: number; // 0-1
  minOpportunityValue: number; // dollars
  agentPriorities: {
    windrose: number; // 0-10 weight
    poniente: number;
    levante: number;
    hookedIn: number;
  };
  enabledOpportunityTypes: {
    revenue: boolean;
    risk: boolean;
    efficiency: boolean;
    growth: boolean;
    compliance: boolean;
  };
}

export interface AppSettings {
  language: Language;
  theme: Theme;
  colorScheme: ColorScheme;
  agentOverrideAuth: boolean;
  simulationMode: boolean;
  linkedAccountsStatus: LinkedAccountsStatus;
  paymentProvidersStatus: PaymentProvidersStatus;
  userRole: 'analyst' | 'manager' | 'cfo' | 'ceo' | 'controller' | 'treasurer';
  persona: 'controller' | 'finance-ops';
  agentMonitoring: AgentMonitoringSettings;
}

const defaultSettings: AppSettings = {
  language: 'en',
  theme: 'light',
  colorScheme: {
    primary: '#3b82f6',
    secondary: '#64748b',
    accent: '#10b981',
    chartColors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
  },
  agentOverrideAuth: false,
  simulationMode: false,
  linkedAccountsStatus: {
    plaid: 'connected',
    belvo: 'connected'
  },
  paymentProvidersStatus: {
    currencycloud: 'connected',
    efex: 'connected',
    thunes: 'connected'
  },
  userRole: 'analyst',
  persona: 'controller',
  agentMonitoring: {
    enabled: true,
    scanInterval: 5, // 5 minutes
    confidenceThreshold: 0.75,
    minOpportunityValue: 1000,
    agentPriorities: {
      windrose: 8, // High priority for financial intelligence
      poniente: 7, // High priority for cash optimization
      levante: 6, // Medium-high for risk monitoring
      hookedIn: 5  // Medium for compliance
    },
    enabledOpportunityTypes: {
      revenue: true,
      risk: true,
      efficiency: true,
      growth: true,
      compliance: true
    }
  }
};

// Lightweight translations for core UI - performance optimized
const coreTranslations: Partial<Record<Language, Record<string, string>>> = {
  en: {
    // Navigation
    'nav.keyChanges': 'Key Changes',
    'nav.whatsDrivingIt': "What's Driving It",
    'nav.takeAction': 'Take Action',
    'nav.arReceivables': 'AR Receivables',
    'nav.invoices': 'Invoices',
    'nav.credits': 'Credits',
    'nav.apPayables': 'AP Payables',
    'nav.suppliers': 'Suppliers',
    'nav.approvals': 'Approvals',
    'nav.paymentsOut': 'Payments Out',
    'nav.paymentsIn': 'Payments In',
    'nav.reconciliation': 'Reconciliation',
    'nav.trends': 'Trends',
    'nav.batchPayments': 'Batch Payments',
    'nav.vendorManagement': 'Vendor Management',
    'nav.overview': 'Overview',
    'nav.details': 'Details',
    'nav.actions': 'Actions',

    // Sidebar
    'sidebar.activities': 'Activities',
    'sidebar.priorities': 'Priorities',
    'sidebar.windroseInsights': 'Windrose Insights',
    'sidebar.alisioRecommendations': 'Alisio Recommendations',
    'sidebar.messages': 'Messages',
    'sidebar.documents': 'Documents',
    'sidebar.contracts': 'Contracts',
    'sidebar.buyers': 'Buyers',
    'sidebar.invoice': 'Invoice',
    'sidebar.paymentsIn': 'Payments In',
    'sidebar.suppliers': 'Suppliers',
    'sidebar.approvals': 'Approvals',
    'sidebar.paymentsOut': 'Payments Out',
    'sidebar.analytics': 'Analytics',
    'sidebar.forex': 'Forex',
    'sidebar.support': 'Support',
    'sidebar.arReceivables': 'AR - Receivables',
    'sidebar.apPayables': 'AP - Payables',
    'sidebar.financialController': 'Financial Controller Platform',

    // Dashboard sections
    'sections.keyChanges': 'Key Changes',
    'sections.whatsDrivingIt': "What's Driving It",
    'sections.takeAction': 'Take Action',

    // Common
    'common.search': 'Search...',
    'common.connected': 'Connected',
    'common.error': 'Error',
    'common.disconnected': 'Disconnected',
    'common.viewAll': 'View All',
    'common.manageApprovals': 'Manage Approvals',
    'common.active': 'Active',

    // Approvals
    'approvals.title': 'Approval Summary',
    'approvals.totalPending': 'Total Pending',
    'approvals.overdue': 'Overdue',
    'approvals.highRisk': 'High Risk',
    'approvals.approved24h': 'Approved (24h)',

    // Pages
    'pages.dashboard': 'Dashboard',
    'pages.activities': 'Activities',
    'pages.messages': 'Messages',
    'pages.documents': 'Documents',
    'pages.contracts': 'Contracts',
    'pages.buyers': 'Buyers',
    'pages.suppliers': 'Suppliers',
    'pages.receivables': 'Receivables',
    'pages.payables': 'Payables',
    'pages.paymentsIn': 'Payments In',
    'pages.paymentsOut': 'Payments Out',
    'pages.analytics': 'Analytics',
    'pages.forex': 'Forex',
    'pages.support': 'Support',
    'pages.priorities': 'Priorities',

    // Charts
    'charts.windroseInsights': 'Windrose Insights',
    'charts.aiRecommendations': 'AI Recommendations',

    // Settings
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your application preferences and integrations',
    'settings.refreshConnections': 'Refresh Connections',
    'settings.languageAppearance': 'Language & Appearance',
    'settings.language': 'Language',
    'settings.theme': 'Theme',
    'settings.colorScheme': 'Color Scheme',
    'settings.agentControls': 'Agent & Simulation Controls',
    'settings.agentAuth': 'Agent Override Authorization',
    'settings.agentOverrideDesc': 'Allow agents to override certain business rules',
    'settings.simulation': 'Simulation Mode',
    'settings.simulationDesc': 'Enable simulation mode for testing scenarios',
    'settings.simulationActive': 'Simulation Mode Active',
    'settings.simulationActiveDesc': 'All actions are simulated and won\'t affect real data',
    'settings.linkedAccounts': 'Linked Accounts',
    'settings.paymentProviders': 'Payment Providers',

    // Languages
    'language.en': 'English',
    'language.es': 'Español',
    'language.jp': '日本語',
    'language.fr-ca': 'Français (Canada)',
    'language.pt-br': 'Português (Brasil)',

    // Themes
    'theme.light': 'Light',
    'theme.dark': 'Dark',

    // Providers
    'provider.plaid': 'Plaid Banking',
    'provider.plaidDesc': 'Bank account connectivity',
    'provider.belvo': 'Belvo',
    'provider.belvoDesc': 'Financial data aggregation',
    'provider.currencycloud': 'Currency Cloud',
    'provider.currencycloudDesc': 'International payments',
    'provider.efex': 'EFEX',
    'provider.efexDesc': 'Foreign exchange services',
    'provider.thunes': 'Thunes',
    'provider.thunesDesc': 'Cross-border payments',

    // Toast messages
    'toast.languageUpdated': 'Language Updated',
    'toast.languageUpdatedDesc': 'Your language preference has been saved',
    'toast.themeUpdated': 'Theme Updated',
    'toast.themeUpdatedDesc': 'Your theme has been changed to {theme}',
    'toast.permissionDenied': 'Permission Denied',
    'toast.permissionDeniedDesc': 'Only CFO and CEO roles can modify agent settings',
    'toast.agentOverrideEnabled': 'Agent Override Enabled',
    'toast.agentOverrideEnabledDesc': 'Agents can now override business rules',
    'toast.agentOverrideDisabled': 'Agent Override Disabled',
    'toast.agentOverrideDisabledDesc': 'Agent override has been disabled',
    'toast.simulationEnabled': 'Simulation Mode Enabled',
    'toast.simulationEnabledDesc': 'All actions will be simulated',
    'toast.simulationDisabled': 'Simulation Mode Disabled',
    'toast.simulationDisabledDesc': 'Actions will affect real data',
    'toast.connectionsRefreshed': 'Connections Refreshed',
    'toast.connectionsRefreshedDesc': 'All integration statuses have been updated',
    'toast.refreshFailed': 'Refresh Failed',
    'toast.refreshFailedDesc': 'Failed to refresh connection status'
  },
  es: {
    // Navigation
    'nav.keyChanges': 'Cambios Clave',
    'nav.whatsDrivingIt': 'Qué lo Impulsa',
    'nav.takeAction': 'Tomar Acción',
    'nav.arReceivables': 'Cuentas por Cobrar',
    'nav.invoices': 'Facturas',
    'nav.credits': 'Créditos',
    'nav.apPayables': 'Cuentas por Pagar',
    'nav.suppliers': 'Proveedores',
    'nav.approvals': 'Aprobaciones',
    'nav.paymentsOut': 'Pagos Salientes',
    'nav.paymentsIn': 'Pagos Entrantes',
    'nav.reconciliation': 'Conciliación',
    'nav.trends': 'Tendencias',
    'nav.batchPayments': 'Pagos en Lote',
    'nav.vendorManagement': 'Gestión de Proveedores',
    'nav.overview': 'Resumen',
    'nav.details': 'Detalles',
    'nav.actions': 'Acciones',

    // Sidebar
    'sidebar.activities': 'Actividades',
    'sidebar.priorities': 'Prioridades',
    'sidebar.windroseInsights': 'Insights de Windrose',
    'sidebar.alisioRecommendations': 'Recomendaciones de Alisio',
    'sidebar.messages': 'Mensajes',
    'sidebar.documents': 'Documentos',
    'sidebar.contracts': 'Contratos',
    'sidebar.buyers': 'Compradores',
    'sidebar.invoice': 'Factura',
    'sidebar.paymentsIn': 'Pagos Entrantes',
    'sidebar.suppliers': 'Proveedores',
    'sidebar.approvals': 'Aprobaciones',
    'sidebar.paymentsOut': 'Pagos Salientes',
    'sidebar.analytics': 'Analíticas',
    'sidebar.forex': 'Divisas',
    'sidebar.support': 'Soporte',
    'sidebar.arReceivables': 'CC - Cuentas por Cobrar',
    'sidebar.apPayables': 'CP - Cuentas por Pagar',
    'sidebar.financialController': 'Plataforma de Controlador Financiero',

    // Dashboard sections
    'sections.keyChanges': 'Cambios Clave',
    'sections.whatsDrivingIt': 'Qué lo Impulsa',
    'sections.takeAction': 'Tomar Acción',

    // Common
    'common.search': 'Buscar...',
    'common.connected': 'Conectado',
    'common.error': 'Error',
    'common.disconnected': 'Desconectado',
    'common.viewAll': 'Ver Todo',
    'common.manageApprovals': 'Gestionar Aprobaciones',
    'common.active': 'Activo',

    // Approvals
    'approvals.title': 'Resumen de Aprobaciones',
    'approvals.totalPending': 'Total Pendiente',
    'approvals.overdue': 'Vencido',
    'approvals.highRisk': 'Alto Riesgo',
    'approvals.approved24h': 'Aprobado (24h)',

    // Pages
    'pages.dashboard': 'Tablero',
    'pages.activities': 'Actividades',
    'pages.messages': 'Mensajes',
    'pages.documents': 'Documentos',
    'pages.contracts': 'Contratos',
    'pages.buyers': 'Compradores',
    'pages.suppliers': 'Proveedores',
    'pages.receivables': 'Cuentas por Cobrar',
    'pages.payables': 'Cuentas por Pagar',
    'pages.paymentsIn': 'Pagos Entrantes',
    'pages.paymentsOut': 'Pagos Salientes',
    'pages.analytics': 'Analíticas',
    'pages.forex': 'Divisas',
    'pages.support': 'Soporte',
    'pages.priorities': 'Prioridades',

    // Charts
    'charts.windroseInsights': 'Insights de Windrose',
    'charts.aiRecommendations': 'Recomendaciones de IA',

    // Settings
    'settings.title': 'Configuración',
    'settings.subtitle': 'Gestiona tus preferencias de aplicación e integraciones',
    'settings.refreshConnections': 'Actualizar Conexiones',
    'settings.languageAppearance': 'Idioma y Apariencia',
    'settings.language': 'Idioma',
    'settings.theme': 'Tema',
    'settings.colorScheme': 'Esquema de Colores',
    'settings.agentControls': 'Controles de Agente y Simulación',
    'settings.agentAuth': 'Autorización de Anulación de Agente',
    'settings.agentOverrideDesc': 'Permitir a los agentes anular ciertas reglas de negocio',
    'settings.simulation': 'Modo Simulación',
    'settings.simulationDesc': 'Habilitar modo simulación para probar escenarios',
    'settings.simulationActive': 'Modo Simulación Activo',
    'settings.simulationActiveDesc': 'Todas las acciones son simuladas y no afectarán datos reales',
    'settings.linkedAccounts': 'Cuentas Vinculadas',
    'settings.paymentProviders': 'Proveedores de Pago',

    // Languages
    'language.en': 'English',
    'language.es': 'Español',
    'language.jp': '日本語',
    'language.fr-ca': 'Français (Canada)',
    'language.pt-br': 'Português (Brasil)',

    // Themes
    'theme.light': 'Claro',
    'theme.dark': 'Oscuro',

    // Providers
    'provider.plaid': 'Banca Plaid',
    'provider.plaidDesc': 'Conectividad de cuentas bancarias',
    'provider.belvo': 'Belvo',
    'provider.belvoDesc': 'Agregación de datos financieros',
    'provider.currencycloud': 'Currency Cloud',
    'provider.currencycloudDesc': 'Pagos internacionales',
    'provider.efex': 'EFEX',
    'provider.efexDesc': 'Servicios de divisas',
    'provider.thunes': 'Thunes',
    'provider.thunesDesc': 'Pagos transfronterizos',

    // Toast messages
    'toast.languageUpdated': 'Idioma Actualizado',
    'toast.languageUpdatedDesc': 'Tu preferencia de idioma ha sido guardada',
    'toast.themeUpdated': 'Tema Actualizado',
    'toast.themeUpdatedDesc': 'Tu tema ha sido cambiado a {theme}',
    'toast.permissionDenied': 'Permiso Denegado',
    'toast.permissionDeniedDesc': 'Solo los roles CFO y CEO pueden modificar configuraciones de agente',
    'toast.agentOverrideEnabled': 'Anulación de Agente Habilitada',
    'toast.agentOverrideEnabledDesc': 'Los agentes ahora pueden anular reglas de negocio',
    'toast.agentOverrideDisabled': 'Anulación de Agente Deshabilitada',
    'toast.agentOverrideDisabledDesc': 'La anulación de agente ha sido deshabilitada',
    'toast.simulationEnabled': 'Modo Simulación Habilitado',
    'toast.simulationEnabledDesc': 'Todas las acciones serán simuladas',
    'toast.simulationDisabled': 'Modo Simulación Deshabilitado',
    'toast.simulationDisabledDesc': 'Las acciones afectarán datos reales',
    'toast.connectionsRefreshed': 'Conexiones Actualizadas',
    'toast.connectionsRefreshedDesc': 'Todos los estados de integración han sido actualizados',
    'toast.refreshFailed': 'Actualización Fallida',
    'toast.refreshFailedDesc': 'Falló al actualizar el estado de conexión'
  },
  'fr-ca': {
    // Navigation
    'nav.keyChanges': 'Changements Clés',
    'nav.whatsDrivingIt': 'Ce qui le Pousse',
    'nav.takeAction': 'Prendre Action',
    'nav.arReceivables': 'Comptes Recevables',
    'nav.invoices': 'Factures',
    'nav.credits': 'Crédits',
    'nav.apPayables': 'Comptes Payables',
    'nav.suppliers': 'Fournisseurs',
    'nav.approvals': 'Approbations',
    'nav.paymentsOut': 'Paiements Sortants',
    'nav.paymentsIn': 'Paiements Entrants',
    'nav.reconciliation': 'Réconciliation',
    'nav.trends': 'Tendances',
    'nav.batchPayments': 'Paiements en Lot',
    'nav.vendorManagement': 'Gestion des Fournisseurs',
    'nav.overview': 'Aperçu',
    'nav.details': 'Détails',
    'nav.actions': 'Actions',

    // Sidebar
    'sidebar.activities': 'Activités',
    'sidebar.priorities': 'Priorités',
    'sidebar.windroseInsights': 'Aperçus Windrose',
    'sidebar.alisioRecommendations': 'Recommandations Alisio',
    'sidebar.messages': 'Messages',
    'sidebar.documents': 'Documents',
    'sidebar.contracts': 'Contrats',
    'sidebar.buyers': 'Acheteurs',
    'sidebar.invoice': 'Facture',
    'sidebar.paymentsIn': 'Paiements Entrants',
    'sidebar.suppliers': 'Fournisseurs',
    'sidebar.approvals': 'Approbations',
    'sidebar.paymentsOut': 'Paiements Sortants',
    'sidebar.analytics': 'Analytiques',
    'sidebar.forex': 'Change',
    'sidebar.support': 'Support',
    'sidebar.arReceivables': 'CR - Comptes Recevables',
    'sidebar.apPayables': 'CP - Comptes Payables',
    'sidebar.financialController': 'Plateforme de Contrôleur Financier',

    // Dashboard sections
    'sections.keyChanges': 'Changements Clés',
    'sections.whatsDrivingIt': 'Ce qui le Pousse',
    'sections.takeAction': 'Prendre Action',

    // Common
    'common.search': 'Rechercher...',
    'common.connected': 'Connecté',
    'common.error': 'Erreur',
    'common.disconnected': 'Déconnecté',
    'common.viewAll': 'Voir Tout',
    'common.manageApprovals': 'Gérer les Approbations',
    'common.active': 'Actif',

    // Approvals
    'approvals.title': 'Résumé des Approbations',
    'approvals.totalPending': 'Total en Attente',
    'approvals.overdue': 'En Retard',
    'approvals.highRisk': 'Haut Risque',
    'approvals.approved24h': 'Approuvé (24h)',

    // Pages
    'pages.dashboard': 'Tableau de Bord',
    'pages.activities': 'Activités',
    'pages.messages': 'Messages',
    'pages.documents': 'Documents',
    'pages.contracts': 'Contrats',
    'pages.buyers': 'Acheteurs',
    'pages.suppliers': 'Fournisseurs',
    'pages.receivables': 'Comptes Recevables',
    'pages.payables': 'Comptes Payables',
    'pages.paymentsIn': 'Paiements Entrants',
    'pages.paymentsOut': 'Paiements Sortants',
    'pages.analytics': 'Analytiques',
    'pages.forex': 'Change',
    'pages.support': 'Support',
    'pages.priorities': 'Priorités',

    // Charts
    'charts.windroseInsights': 'Aperçus Windrose',
    'charts.aiRecommendations': 'Recommandations IA',

    // Settings
    'settings.title': 'Paramètres',
    'settings.subtitle': 'Gérez vos préférences d\'application et intégrations',
    'settings.refreshConnections': 'Actualiser les Connexions',
    'settings.languageAppearance': 'Langue et Apparence',
    'settings.language': 'Langue',
    'settings.theme': 'Thème',
    'settings.colorScheme': 'Schéma de Couleurs',
    'settings.agentControls': 'Contrôles d\'Agent et Simulation',
    'settings.agentAuth': 'Autorisation de Remplacement d\'Agent',
    'settings.agentOverrideDesc': 'Permettre aux agents de remplacer certaines règles d\'affaires',
    'settings.simulation': 'Mode Simulation',
    'settings.simulationDesc': 'Activer le mode simulation pour tester des scénarios',
    'settings.simulationActive': 'Mode Simulation Actif',
    'settings.simulationActiveDesc': 'Toutes les actions sont simulées et n\'affecteront pas les données réelles',
    'settings.linkedAccounts': 'Comptes Liés',
    'settings.paymentProviders': 'Fournisseurs de Paiement',

    // Languages
    'language.en': 'English',
    'language.es': 'Español',
    'language.jp': '日本語',
    'language.fr-ca': 'Français (Canada)',
    'language.pt-br': 'Português (Brasil)',

    // Themes
    'theme.light': 'Clair',
    'theme.dark': 'Sombre',

    // Providers
    'provider.plaid': 'Banque Plaid',
    'provider.plaidDesc': 'Connectivité des comptes bancaires',
    'provider.belvo': 'Belvo',
    'provider.belvoDesc': 'Agrégation de données financières',
    'provider.currencycloud': 'Currency Cloud',
    'provider.currencycloudDesc': 'Paiements internationaux',
    'provider.efex': 'EFEX',
    'provider.efexDesc': 'Services de change',
    'provider.thunes': 'Thunes',
    'provider.thunesDesc': 'Paiements transfronterizos',

    // Toast messages
    'toast.languageUpdated': 'Langue Mise à Jour',
    'toast.languageUpdatedDesc': 'Votre préférence de langue a été sauvegardée',
    'toast.themeUpdated': 'Thème Mis à Jour',
    'toast.themeUpdatedDesc': 'Votre thème a été changé pour {theme}',
    'toast.permissionDenied': 'Permission Refusée',
    'toast.permissionDeniedDesc': 'Seuls les rôles CFO et CEO peuvent modifier les paramètres d\'agent',
    'toast.agentOverrideEnabled': 'Remplacement d\'Agent Activé',
    'toast.agentOverrideEnabledDesc': 'Les agents peuvent maintenant remplacer les règles d\'affaires',
    'toast.agentOverrideDisabled': 'Remplacement d\'Agent Désactivé',
    'toast.agentOverrideDisabledDesc': 'Le remplacement d\'agent a été désactivé',
    'toast.simulationEnabled': 'Mode Simulation Activé',
    'toast.simulationEnabledDesc': 'Toutes les actions seront simulées',
    'toast.simulationDisabled': 'Mode Simulation Désactivé',
    'toast.simulationDisabledDesc': 'Les actions affecteront les données réelles',
    'toast.connectionsRefreshed': 'Connexions Actualisées',
    'toast.connectionsRefreshedDesc': 'Tous les statuts d\'intégration ont été mis à jour',
    'toast.refreshFailed': 'Actualisation Échouée',
    'toast.refreshFailedDesc': 'Échec de l\'actualisation du statut de connexion'
  },
  'pt-br': {
    // Navigation
    'nav.keyChanges': 'Mudanças Principais',
    'nav.whatsDrivingIt': 'O que está Impulsionando',
    'nav.takeAction': 'Tomar Ação',
    'nav.arReceivables': 'Contas a Receber',
    'nav.invoices': 'Faturas',
    'nav.credits': 'Créditos',
    'nav.apPayables': 'Contas a Pagar',
    'nav.suppliers': 'Fornecedores',
    'nav.approvals': 'Aprovações',
    'nav.paymentsOut': 'Pagamentos Saídas',
    'nav.paymentsIn': 'Pagamentos Entradas',
    'nav.reconciliation': 'Reconciliação',
    'nav.trends': 'Tendências',
    'nav.batchPayments': 'Pagamentos em Lote',
    'nav.vendorManagement': 'Gestão de Fornecedores',
    'nav.overview': 'Visão Geral',
    'nav.details': 'Detalhes',
    'nav.actions': 'Ações',

    // Sidebar
    'sidebar.activities': 'Atividades',
    'sidebar.priorities': 'Prioridades',
    'sidebar.windroseInsights': 'Insights Windrose',
    'sidebar.alisioRecommendations': 'Recomendações Alisio',
    'sidebar.messages': 'Mensagens',
    'sidebar.documents': 'Documentos',
    'sidebar.contracts': 'Contratos',
    'sidebar.buyers': 'Compradores',
    'sidebar.invoice': 'Fatura',
    'sidebar.paymentsIn': 'Pagamentos Entradas',
    'sidebar.suppliers': 'Fornecedores',
    'sidebar.approvals': 'Aprovações',
    'sidebar.paymentsOut': 'Pagamentos Saídas',
    'sidebar.analytics': 'Análises',
    'sidebar.forex': 'Câmbio',
    'sidebar.support': 'Suporte',
    'sidebar.arReceivables': 'CR - Contas a Receber',
    'sidebar.apPayables': 'CP - Contas a Pagar',
    'sidebar.financialController': 'Plataforma de Controlador Financeiro',

    // Dashboard sections
    'sections.keyChanges': 'Mudanças Principais',
    'sections.whatsDrivingIt': 'O que está Impulsionando',
    'sections.takeAction': 'Tomar Ação',

    // Common
    'common.search': 'Pesquisar...',
    'common.connected': 'Conectado',
    'common.error': 'Erro',
    'common.disconnected': 'Desconectado',
    'common.viewAll': 'Ver Tudo',
    'common.manageApprovals': 'Gerenciar Aprovações',
    'common.active': 'Ativo',

    // Approvals
    'approvals.title': 'Resumo de Aprovações',
    'approvals.totalPending': 'Total Pendente',
    'approvals.overdue': 'Vencido',
    'approvals.highRisk': 'Alto Risco',
    'approvals.approved24h': 'Aprovado (24h)',

    // Pages
    'pages.dashboard': 'Painel',
    'pages.activities': 'Atividades',
    'pages.messages': 'Mensagens',
    'pages.documents': 'Documentos',
    'pages.contracts': 'Contratos',
    'pages.buyers': 'Compradores',
    'pages.suppliers': 'Fornecedores',
    'pages.receivables': 'Contas a Receber',
    'pages.payables': 'Contas a Pagar',
    'pages.paymentsIn': 'Pagamentos Entradas',
    'pages.paymentsOut': 'Pagamentos Saídas',
    'pages.analytics': 'Análises',
    'pages.forex': 'Câmbio',
    'pages.support': 'Suporte',
    'pages.priorities': 'Prioridades',

    // Charts
    'charts.windroseInsights': 'Insights Windrose',
    'charts.aiRecommendations': 'Recomendações de IA',

    // Settings
    'settings.title': 'Configurações',
    'settings.subtitle': 'Gerencie suas preferências de aplicativo e integrações',
    'settings.refreshConnections': 'Atualizar Conexões',
    'settings.languageAppearance': 'Idioma e Aparência',
    'settings.language': 'Idioma',
    'settings.theme': 'Tema',
    'settings.colorScheme': 'Esquema de Cores',
    'settings.agentControls': 'Controles de Agente e Simulação',
    'settings.agentAuth': 'Autorização de Substituição de Agente',
    'settings.agentOverrideDesc': 'Permitir que agentes substituam certas regras de negócio',
    'settings.simulation': 'Modo Simulação',
    'settings.simulationDesc': 'Habilitar modo simulação para testar cenários',
    'settings.simulationActive': 'Modo Simulação Ativo',
    'settings.simulationActiveDesc': 'Todas as ações são simuladas e não afetarão dados reais',
    'settings.linkedAccounts': 'Contas Vinculadas',
    'settings.paymentProviders': 'Provedores de Pagamento',

    // Languages
    'language.en': 'English',
    'language.es': 'Español',
    'language.jp': '日本語',
    'language.fr-ca': 'Français (Canada)',
    'language.pt-br': 'Português (Brasil)',

    // Themes
    'theme.light': 'Claro',
    'theme.dark': 'Escuro',

    // Providers
    'provider.plaid': 'Banco Plaid',
    'provider.plaidDesc': 'Conectividade de contas bancárias',
    'provider.belvo': 'Belvo',
    'provider.belvoDesc': 'Agregação de dados financeiros',
    'provider.currencycloud': 'Currency Cloud',
    'provider.currencycloudDesc': 'Pagamentos internacionais',
    'provider.efex': 'EFEX',
    'provider.efexDesc': 'Serviços de câmbio',
    'provider.thunes': 'Thunes',
    'provider.thunesDesc': 'Pagamentos transfronteiriços',

    // Toast messages
    'toast.languageUpdated': 'Idioma Atualizado',
    'toast.languageUpdatedDesc': 'Sua preferência de idioma foi salva',
    'toast.themeUpdated': 'Tema Atualizado',
    'toast.themeUpdatedDesc': 'Seu tema foi alterado para {theme}',
    'toast.permissionDenied': 'Permissão Negada',
    'toast.permissionDeniedDesc': 'Apenas funções CFO e CEO podem modificar configurações de agente',
    'toast.agentOverrideEnabled': 'Substituição de Agente Habilitada',
    'toast.agentOverrideEnabledDesc': 'Agentes agora podem substituir regras de negócio',
    'toast.agentOverrideDisabled': 'Substituição de Agente Desabilitada',
    'toast.agentOverrideDisabledDesc': 'Substituição de agente foi desabilitada',
    'toast.simulationEnabled': 'Modo Simulação Habilitado',
    'toast.simulationEnabledDesc': 'Todas as ações serão simuladas',
    'toast.simulationDisabled': 'Modo Simulação Desabilitado',
    'toast.simulationDisabledDesc': 'Ações afetarão dados reais',
    'toast.connectionsRefreshed': 'Conexões Atualizadas',
    'toast.connectionsRefreshedDesc': 'Todos os status de integração foram atualizados',
    'toast.refreshFailed': 'Atualização Falhou',
    'toast.refreshFailedDesc': 'Falha ao atualizar status de conexão'
  }
};

interface SettingsContextType {
  settings: AppSettings;
  updateSettings: (newSettings: Partial<AppSettings>) => void;
  t: (key: string) => string;
  refreshConnectionStatus: () => Promise<void>;
  isLoading: boolean;
  persona: 'controller' | 'finance-ops';
  setPersona: (persona: 'controller' | 'finance-ops') => void;
  isDemoMode: boolean;
  toggleDemoMode: () => void;
  setDemoMode: (enabled: boolean) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const [isDemoMode, setIsDemoMode] = useState<boolean>(() => {
    // Check both storage locations for demo mode
    const localStored = localStorage.getItem('alisio-demo-mode');
    const sessionStored = sessionStorage.getItem('demo_mode');

    // Prioritize sessionStorage demo_mode=true (set during active login flow in DemoAccountSetup)
    // This prevents localStorage stale false from overwriting a fresh demo login
    if (sessionStored === 'true') {
      return true;
    }
    if (localStored !== null) {
      return JSON.parse(localStored);
    }
    return false;
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('app-settings');
    let loaded = { ...defaultSettings };
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        loaded = { ...defaultSettings, ...parsed };
      } catch (error) {
        console.error('Failed to parse saved settings:', error);
      }
    }
    // Auto-set Spanish for MX demo region — ONLY when the user has never
    // chosen a language (no persisted app-settings.language). An explicit
    // choice in Settings must survive reloads; reverting it on every mount
    // was BUG-29 ("global language change has not persisted").
    //
    // This is the ONE place that must keep the raw sessionStorage read rather
    // than calling getDemoRegion(). The resolver's last fallback is
    // `app-settings.language === 'es' → 'mx'`, and this block decides that very
    // language — routing it through the resolver would make the language derive
    // from itself. Every other region read in the app goes through
    // getDemoRegion(); this one is deliberately the seed of that chain.
    const demoRegion = sessionStorage.getItem('demo_region');
    let persistedLanguage: string | undefined;
    try {
      persistedLanguage = savedSettings ? JSON.parse(savedSettings).language : undefined;
    } catch {
      persistedLanguage = undefined;
    }
    if (demoRegion === 'mx' && !persistedLanguage) {
      loaded.language = 'es';
      localStorage.setItem('app-settings', JSON.stringify(loaded));
    }
    setSettings(loaded);
  }, []);

  // Demo mode effects - sync both localStorage and sessionStorage
  useEffect(() => {
    localStorage.setItem('alisio-demo-mode', JSON.stringify(isDemoMode));
    // Also sync to sessionStorage for backward compatibility with existing code
    sessionStorage.setItem('demo_mode', isDemoMode ? 'true' : 'false');
  }, [isDemoMode]);

  const updateSettings = (newSettings: Partial<AppSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    // BUG-23: swap the UI first, persist after. setSettings triggers the
    // re-render (language swap); the localStorage write is deferred to a later
    // task so JSON.stringify + persistence never block the visible update —
    // the user sees the new language immediately and the save runs in the
    // background.
    setSettings(updatedSettings);
    setTimeout(() => {
      try {
        localStorage.setItem('app-settings', JSON.stringify(updatedSettings));
      } catch (err) {
        console.error('Failed to persist settings:', err);
      }
    }, 0);
    if (newSettings.language) {
      console.log('Language changed to:', newSettings.language);
    }
  };

  // Performance-optimized translation function
  const t = (key: string): string => {
    return coreTranslations[settings.language]?.[key] || key;
  };

  const refreshConnectionStatus = async () => {
    setIsLoading(true);
    try {
      // Simulate API call to refresh connection status
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real app, this would make actual API calls to check status
      const statuses: ('connected' | 'disconnected' | 'error')[] = ['connected', 'disconnected', 'error'];
      const getRandomStatus = () => statuses[Math.floor(Math.random() * statuses.length)];
      
      const newStatus = {
        linkedAccountsStatus: {
          plaid: Math.random() > 0.2 ? 'connected' as const : getRandomStatus(),
          belvo: Math.random() > 0.2 ? 'connected' as const : getRandomStatus()
        },
        paymentProvidersStatus: {
          currencycloud: Math.random() > 0.2 ? 'connected' as const : getRandomStatus(),
          efex: Math.random() > 0.2 ? 'connected' as const : getRandomStatus(),
          thunes: Math.random() > 0.2 ? 'connected' as const : getRandomStatus()
        }
      };
      
      updateSettings(newStatus);
      console.log('Connection status refreshed');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDemoMode = async () => {
    const newDemoMode = !isDemoMode;

    if (newDemoMode) {
      // Entering demo mode - sign in as demo user
      try {
        const result = await authClient.signIn.email({
          email: 'demo@alisiofi.com',
          password: 'Demo@Password2025!'
        });

        if (result.error) {
          const error = result.error;
          console.error('Demo sign-in failed:', error);
          toast({
            title: 'Demo Mode Error',
            description: 'Could not sign in to demo account. Please contact support.',
            variant: 'destructive'
          });
          return;
        }

        console.log('Demo mode: Signed in as demo user');
        setIsDemoMode(true);
      } catch (error) {
        console.error('Demo mode error:', error);
        toast({
          title: 'Demo Mode Error',
          description: 'Could not activate demo mode.',
          variant: 'destructive'
        });
      }
    } else {
      // Exiting demo mode - sign out
      await authClient.signOut();
      setIsDemoMode(false);
      window.location.reload(); // Refresh to clear state
    }
  };

  const setDemoMode = async (enabled: boolean) => {
    if (enabled === isDemoMode) return;
    await toggleDemoMode();
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSettings,
      t,
      refreshConnectionStatus,
      isLoading,
      persona: settings.persona,
      setPersona: (persona: 'controller' | 'finance-ops') => updateSettings({ persona }),
      isDemoMode,
      toggleDemoMode,
      setDemoMode,
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
